import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  X,
  AlertTriangle,
  ChevronDown,
  CheckCircle2,
  XCircle,
  UserPlus,
  FilePlus,
  Pencil,
  Clipboard,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import API_URL from '@/lib/apiConfig';
import { usePermissions } from '@/hooks/usePermissions';
import { FieldGrid } from '@/components/match/board/FieldGrid';
import { FIELDS_BY_TAB, TABS } from '@/lib/match/contractH';
import { FlagChip } from '@/components/match/board/FlagChip';
import { ConfidenceBar } from '@/components/match/board/ConfidenceBar';
import CreateNewCustomerModal from '@/components/match/board/CreateNewCustomerModal';
import CreateNewOrderModal from '@/components/match/board/CreateNewOrderModal';
import CompleteRequiredFieldsModal from '@/components/match/board/CompleteRequiredFieldsModal';
import PushHistorySection from '@/components/match/board/PushHistorySection';
import ScoreBreakdown from '@/components/match/board/ScoreBreakdown';
import CandidatesConsidered from '@/components/match/board/CandidatesConsidered';
import ConfirmModal from '@/components/shared/ConfirmModal';

// ── Helpers ───────────────────────────────────────────────────────────────────

function getDot(obj, path) {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
}

function setDot(obj, path, value) {
  const parts = path.split('.');
  const last  = parts.pop();
  const next  = JSON.parse(JSON.stringify(obj || {}));
  let cur     = next;
  for (const p of parts) {
    if (!(p in cur) || typeof cur[p] !== 'object' || cur[p] === null) cur[p] = {};
    cur = cur[p];
  }
  cur[last] = value;
  return next;
}

// Status display helpers ──────────────────────────────────────────────────────

const STATUS_LABEL = {
  ready:            'Ready',
  ready_to_draft:   'Ready to Draft',
  needs_review:     'Needs Review',
  approved:         'Approved',
  edited:           'Edited',
  rejected:         'Rejected',
  escalated:        'Escalated',
  create_new:       'Create New',
  create_new_order: 'Create New Order',
  error:            'Error',
};

// Confirm-before-push copy — active_env / live_post_enabled now ride on the
// row itself (GET /match/rows/{id} added appdirect_active_env /
// appdirect_live_post_enabled; see match_row.py::_load_appdirect_env_flags).
// Four distinct situations, and the wording has to stay honest in each —
// this dialog is the last checkpoint before a real AppDirect write.
const PUSH_CONFIRM_TITLE = {
  approved:         'Push this match to AppDirect?',
  edited:           'Push this match to AppDirect?',
  create_new:       'Create customer + order in AppDirect?',
  create_new_order: 'Create this order in AppDirect?',
};

// row.appdirect_active_env is '' — not 'sandbox' — when the tenant has no
// settings doc or no appdirect block. That's deliberate on the backend side
// so '' means UNKNOWN, not "assume sandbox." Same treatment for any row
// fetched before this contract shipped, where the field is simply absent.
// Never guess sandbox for either case — an unconfigured tenant showing
// "Sandbox" would be a confident-sounding lie, worse than admitting we don't
// know. live_post_enabled has no such ambiguity: the backend already
// defaults it to false when unset, so an absent value here is honestly "off."
function pushConfirmCopy(action, row) {
  const rawEnv = row?.appdirect_active_env;
  const env = rawEnv === 'prod' || rawEnv === 'sandbox' ? rawEnv : null;
  const livePostEnabled = !!row?.appdirect_live_post_enabled;
  const baseTitle = (PUSH_CONFIRM_TITLE[action] || 'Push to AppDirect?').replace(/\?\s*$/, '');

  // Unknown environment — covers '' (no settings doc / no appdirect block)
  // and rows fetched before this contract shipped. Keep the old honest
  // fallback: we can't see it from here, go check Settings first.
  if (!env) {
    return {
      title: `${baseTitle}?`,
      variant: 'warning',
      confirmText: 'Push to AppDirect',
      message:
        "This dialog can't tell which AppDirect environment or Live Posting " +
        "setting is active for this tenant — check Settings → AppDirect first " +
        "if you aren't sure.\n\n" +
        'If Live Posting is OFF, AppDirect returns a dry-run preview and ' +
        "nothing is booked. If it's ON, this creates or updates a live, " +
        'billable order.',
    };
  }

  const envLabel = env === 'prod' ? 'PRODUCTION' : 'Sandbox';

  // Production + Live Posting on — the dangerous one. This is the only state
  // that uses the danger variant, and the only one that names PRODUCTION in
  // the title so a skim-reader can't miss it.
  if (env === 'prod' && livePostEnabled) {
    return {
      title: `${baseTitle} — PRODUCTION?`,
      variant: 'danger',
      confirmText: 'Push to PRODUCTION',
      message:
        'This creates or updates a REAL, BILLABLE order in the PRODUCTION ' +
        'AppDirect marketplace. Live Posting is ON for this tenant.\n\n' +
        'Double-check the customer, product, and pricing before continuing ' +
        '— this books an actual order.',
    };
  }

  // Live Posting off, either environment — dry-run preview, nothing booked.
  // Say that plainly so this safe action doesn't read as scary.
  if (!livePostEnabled) {
    return {
      title: `${baseTitle} — ${envLabel}?`,
      variant: 'primary',
      confirmText: 'Push to AppDirect',
      message:
        `Live Posting is OFF for this tenant, so this runs as a dry-run ` +
        `preview against the ${env === 'prod' ? 'production' : 'sandbox'} ` +
        'AppDirect environment — nothing will be created or billed.',
    };
  }

  // Sandbox + Live Posting on — the ordinary case. State the environment
  // plainly, no alarming language.
  return {
    title: `${baseTitle} — ${envLabel}?`,
    variant: 'primary',
    confirmText: 'Push to AppDirect',
    message:
      'This sends a real request to the AppDirect Sandbox environment. ' +
      'Live Posting is ON, so this creates or updates an order there.',
  };
}

function statusClass(status) {
  switch (status) {
    case 'approved':
    case 'edited':
    case 'create_new':
    case 'create_new_order':
      return 'bg-[var(--boss-success-soft)] text-[var(--boss-success-text)] border-[var(--boss-success-border)]';
    case 'needs_review':
      return 'bg-[var(--boss-warning-soft)] text-[var(--boss-warning-text)] border-[var(--boss-warning-border)]';
    case 'rejected':
    case 'error':
      return 'bg-[var(--boss-danger-soft)] text-[var(--boss-danger-text)] border-[var(--boss-danger-border)]';
    case 'escalated':
      return 'bg-[var(--boss-warning-soft)] text-[var(--boss-warning-text)] border-[var(--boss-warning-border)]';
    default:
      return 'bg-boss-elevated text-boss-text-muted border-boss-border';
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * RowDetailDrawer — schema-driven 5-tab side-by-side match review.
 *
 * Props:
 *   open          {boolean}        — controlled by BoardTab
 *   onOpenChange  {(boolean)=>void}
 *   rowId         {string|null}    — m_row_… or null
 *   onActioned    {()=>void}       — parent refetches rows after action
 */
export default function RowDetailDrawer({ open, onOpenChange, rowId, onActioned }) {
  // ── Data ────────────────────────────────────────────────────────────────────
  const [row,       setRow]       = useState(null);
  const [edited,    setEdited]    = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [activeTab, setActiveTab] = useState('top_level');

  // Sales-rep combobox — cached by advisor name, null = not yet loaded
  // { [advisorName]: { advisor: obj|null, items: [] } }
  const [salesRepsCache, setSalesRepsCache] = useState({});

  // ── Action state ────────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);

  // Pending push confirmation — gates every action that chains into a real
  // AppDirect write (see PUSH_AFTER_ACTIONS below). null = no pending action;
  // { action, extras } = ConfirmModal is open, waiting on the operator.
  const [pendingPush, setPendingPush] = useState(null);

  // Product-catalog refresh (Task 3) — in-flight flag for the "Refresh
  // product catalog" action so the button disables and errors surface.
  const [refreshingProducts, setRefreshingProducts] = useState(false);

  // Reason dialog (Dismiss)
  const [reasonDialog, setReasonDialog] = useState({ open: false, action: null });
  const [reasonText,   setReasonText]   = useState('');

  // Create New Customer modal
  const [createCustomerModal, setCreateCustomerModal] = useState({
    open: false, draft: null,
  });

  // Create New Order modal
  const [createOrderModal, setCreateOrderModal] = useState({
    open: false, draft: null,
  });

  // Complete-required-fields modal — the fill-in-place step of the confirm
  // flow. Opens when /push-readiness says the pending action would fail.
  const [fillModal, setFillModal] = useState({
    open: false, readiness: null, action: null, extras: null,
  });

  const { hasPermission } = usePermissions();

  // ── Fetch row (on open + after a push so the stamped PS order id shows) ─────
  async function loadRow(id, { keepEdits = false } = {}) {
    const res = await api.get(`${API_URL}/api/match/rows/${id}`);
    if (res.ok) {
      const data = await res.json();
      setRow(data);
      if (!keepEdits) {
        const initial = structuredClone(data.output || {});
        // Prefill the AppDirect customer id from the match — the id is
        // already known (audit_metadata.matched_candidate_id, the number in
        // the header chip) but n8n leaves the output field blank; reviewers
        // were left retyping a value BOSS already had. Marks the row
        // "edited", which is the truth: the outgoing record changed.
        // ...but never from a new_customer-flagged row: there the candidate id
        // is the matcher's best REJECTED neighbor (21%-confidence lookalike),
        // and prefilling it says "existing customer" about a row the matcher
        // ruled new (live 2026-07-10, "ASSOCIATES AGENCY, INC." / id 51598).
        const mid = data.audit_metadata?.matched_candidate_id;
        const isNewCust = (data.audit_metadata?.flags || []).includes('new_customer');
        if (mid && !isNewCust && !initial?.top_level?.customer?.appdirect_customer_id) {
          initial.top_level = initial.top_level || {};
          initial.top_level.customer = initial.top_level.customer || {};
          initial.top_level.customer.appdirect_customer_id = String(mid);
        }
        setEdited(initial);
      }
      // Pre-fetch sales reps for the row's advisor when we have one
      const advisorName = data.output?.top_level?.advisor_company || '';
      if (advisorName) {
        fetchSalesReps(advisorName);
      }
      return data;
    }
    const err = await res.json().catch(() => ({}));
    toast.error(err?.detail || 'Failed to load row');
    return null;
  }

  // Fetch sales reps for a given advisor name — cached per name.
  async function fetchSalesReps(advisorName) {
    if (!advisorName) return;
    if (salesRepsCache[advisorName] !== undefined) return; // already cached
    try {
      const res = await api.get(
        `${API_URL}/api/match/appdirect/sales-reps?advisor_name=${encodeURIComponent(advisorName)}`
      );
      if (res.ok) {
        const data = await res.json();
        setSalesRepsCache((prev) => ({ ...prev, [advisorName]: data }));
      } else {
        // On failure, cache an empty result so we don't retry endlessly
        setSalesRepsCache((prev) => ({ ...prev, [advisorName]: { advisor: null, items: [] } }));
      }
    } catch {
      setSalesRepsCache((prev) => ({ ...prev, [advisorName]: { advisor: null, items: [] } }));
    }
  }

  useEffect(() => {
    if (!open || !rowId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      if (!cancelled) await loadRow(rowId);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rowId]);

  // ── Derived ─────────────────────────────────────────────────────────────────
  const isEdited = useMemo(() => {
    if (!row || !edited) return false;
    return JSON.stringify(row.output || {}) !== JSON.stringify(edited);
  }, [row, edited]);

  const isNewCustomer = useMemo(() => {
    if (!row) return false;
    return (
      (row.audit_metadata?.flags || []).includes('new_customer') ||
      !row.audit_metadata?.matched_candidate_id
    );
  }, [row]);

  const matchedCandidateId = row?.audit_metadata?.matched_candidate_id || null;

  const detailId = row
    ? `DT-${String(row.row_index ?? 0).padStart(6, '0')}`
    : '';

  // Header address — prefer AI-populated location, fall back to source row
  const headerAddress = useMemo(() => {
    if (!row) return null;
    const out = row.output || {};
    const src = row.source_row || {};
    const street =
      getDot(out, 'top_level.location.street') ||
      src['Billing Address'] || src['Service Address'] || src['Street'] || src['street'];
    const city =
      getDot(out, 'top_level.location.city') ||
      src['Billing City'] || src['Service City'] || src['City'] || src['city'];
    const state =
      getDot(out, 'top_level.location.state') ||
      src['Billing State/Prov'] || src['Service State'] || src['State'] || src['state'];
    const zip =
      getDot(out, 'top_level.location.zip') ||
      src['Billing Zip'] || src['Service Zip'] || src['Zip'] || src['ZIP'] || src['zip'];
    const parts = [street, [city, state].filter(Boolean).join(', '), zip].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : null;
  }, [row]);

  // ── Reset helper ─────────────────────────────────────────────────────────────
  function reset() {
    setRow(null);
    setEdited(null);
    setReasonText('');
    setActiveTab('top_level');
    setReasonDialog({ open: false, action: null });
    setCreateCustomerModal({ open: false, draft: null });
    setCreateOrderModal({ open: false, draft: null });
    setFillModal({ open: false, readiness: null, action: null, extras: null });
    setPendingPush(null);
  }

  // ── Derived: product & sales-rep context for the field comboboxes ────────────
  const providerProducts = row?.provider_products || [];
  const productMatch     = row?.audit_metadata?.product_match || null;
  const advisorCompany   = row?.output?.top_level?.advisor_company || '';
  const salesReps        = salesRepsCache[advisorCompany] ?? null; // null = loading

  // Honest-empty-state inputs (Task 2) — every one of these fields may be
  // absent on rows fetched before this contract shipped; default accordingly.
  const providerProductsError    = row?.provider_products_error || null;
  const providerProductsFetchedAt = row?.provider_products_fetched_at || null;
  const appdirectAuthFailed      = !!row?.appdirect_auth_failed;
  const appdirectAuthMessage     = row?.appdirect_auth_message || '';

  // One message, computed once, threaded to every combobox/empty-state that
  // renders "no products" — replaces the honest-when-true "list is just
  // empty" copy with the honest-when-true "we never successfully asked" copy.
  const catalogWarning = appdirectAuthFailed
    ? (appdirectAuthMessage ||
        "Couldn't load the product catalog — AppDirect rejected our session when this report was processed. Re-authenticate in Settings → AppDirect and re-run this session.")
    : providerProductsError
      ? `Couldn't load the product catalog — ${providerProductsError}. Re-run this session once the issue is resolved, or refresh below.`
      : null;

  // ── Refresh product catalog (Task 3b) ───────────────────────────────────────
  // Recovers from a stale/failed catalog fetch without re-running the whole
  // session — provider_products was previously frozen at session-trigger time.
  async function handleRefreshProducts() {
    if (!row?.session_id || refreshingProducts) return;
    setRefreshingProducts(true);
    try {
      const res = await api.post(
        `${API_URL}/api/match/sessions/${row.session_id}/refresh-products`
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data?.detail || 'Failed to refresh product catalog');
        return;
      }
      // Take the auth flags from the response, not from `cur` — the endpoint
      // re-detects them. Carrying the stale trigger-time value forward would
      // leave the "AppDirect rejected our session" warning on screen next to a
      // freshly-loaded catalog, since catalogWarning tests that flag first.
      setRow((cur) => cur && {
        ...cur,
        provider_products:            data.provider_products ?? cur.provider_products,
        provider_products_fetched_at: data.provider_products_fetched_at ?? null,
        provider_products_error:      data.provider_products_error ?? null,
        appdirect_auth_failed:        data.appdirect_auth_failed ?? false,
        appdirect_auth_message:       data.appdirect_auth_message ?? '',
      });
      toast.success('Product catalog refreshed');
    } catch {
      toast.error('Failed to refresh product catalog');
    } finally {
      setRefreshingProducts(false);
    }
  }

  // ── Product selection → PATCH row, then refresh ──────────────────────────────
  async function handleProductSelect(productId, productName) {
    if (!rowId) return;
    const res = await api.patch(`${API_URL}/api/match/rows/${rowId}`, {
      product_id:   productId,
      product_name: productName,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      toast.error(err?.detail || 'Failed to update product');
      return;
    }
    // Update local edited state optimistically (row refresh will confirm)
    setEdited((cur) => setDot(cur || {}, 'order_details.product', productName));
    await loadRow(rowId, { keepEdits: true });
  }

  // ── Sales-rep selection → PATCH row ──────────────────────────────────────────
  async function handleSalesRepSelect(repId, repName) {
    if (!rowId) return;
    const res = await api.patch(`${API_URL}/api/match/rows/${rowId}`, {
      sales_rep_id:   repId,
      sales_rep_name: repName,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      toast.error(err?.detail || 'Failed to update sales rep');
      return;
    }
    setEdited((cur) => setDot(cur || {}, 'top_level.sales_rep', repName));
  }

  // Actions that make a row pushable — these chain straight into the AppDirect
  // push below. Rationale (Aidan, demo day): "if someone clicks create, then
  // has a modal and clicks create again, they would expect something to be
  // created." Approving/creating without booking was a two-step trap with the
  // second step (push) not even exposed in the UI.
  const PUSH_AFTER_ACTIONS = new Set(['approved', 'edited', 'create_new', 'create_new_order']);

  // ── Push to AppDirect ───────────────────────────────────────────────────────
  // Fires the real booking (update-or-create). The response carries the
  // stamped PS order id; we refetch the row so the header pill + Push History
  // update in place. Server-side live_post_enabled still governs whether the
  // write is real — a dry-run result is surfaced honestly.
  async function pushRow() {
    const res = await api.post(
      `${API_URL}/api/match/rows/${rowId}/push-to-appdirect`,
      { dry_run: false }
    );
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.ok) {
      toast.success(
        data.appdirect_order_id
          ? `Booked in AppDirect — PS Order ${data.appdirect_order_id}`
          : 'Booked in AppDirect'
      );
      return true;
    }
    if (data.mode === 'dry_run') {
      toast.warning(
        'Dry run only — Live Posting is OFF in Settings, nothing was written to AppDirect.'
      );
      return false;
    }
    toast.error(
      `Approved — but the AppDirect push failed${data.status_code ? ` (HTTP ${data.status_code})` : ''}` +
        `${data.error ? `: ${String(data.error).slice(0, 140)}` : ' — see Push History on this row.'}`
    );
    return false;
  }

  // ── Push readiness (fill-in-place confirm) ──────────────────────────────────
  // Asks the server what the push would need BEFORE recording the action.
  // Fails open (null) — the server-side pre-flight guards still protect the
  // push itself; readiness only decides whether to collect fields first.
  async function checkReadiness(action, effectiveEdited) {
    try {
      const res = await api.post(
        `${API_URL}/api/match/rows/${rowId}/push-readiness`,
        { action, edited_output: effectiveEdited || null }
      );
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }

  // ── Action handler ──────────────────────────────────────────────────────────
  async function handleAction(action, extras = {}) {
    if (!rowId) return;
    setSubmitting(true);
    try {
      const body = { action, ...extras };
      if (isEdited && !body.edited_output) {
        body.edited_output = edited;
      }
      const willCheckPush = PUSH_AFTER_ACTIONS.has(action) && hasPermission('match.row.edit');

      if (willCheckPush) {
        const readiness = await checkReadiness(action, body.edited_output || null);
        if (readiness && (readiness.missing || []).length > 0) {
          // Collect the missing fields where the user confirms — instead of a
          // 422 after the click. handleFillConfirm re-enters handleAction.
          setFillModal({ open: true, readiness, action, extras });
          return;
        }
        // Surface the server's autofill on the row (e.g. the sales rep from
        // the customer's most recent order) so the drawer shows what the push
        // will use — the push itself resolves the same value server-side.
        const af = readiness?.autofill;
        if (af?.sales_rep_id && !row?.sales_rep_id) {
          await api.patch(`${API_URL}/api/match/rows/${rowId}`, {
            sales_rep_id: af.sales_rep_id,
            sales_rep_name: af.sales_rep_name,
          }).catch(() => {});
        }
      }

      const res = await api.post(`${API_URL}/api/match/rows/${rowId}/action`, body);
      if (!res.ok) {
        const err = await res.json();
        toast.error(asDetailText(err?.detail) || 'Action failed');
        return;
      }
      // When the action chains into a push, the push result is THE toast —
      // firing both produced two overlapping (and on failure, contradicting)
      // messages: "approved ✓" then "push failed ✗" (live 2026-07-10,
      // Catalina Entertainment).
      const willPush = PUSH_AFTER_ACTIONS.has(action) && hasPermission('match.row.edit');
      if (!willPush) toast.success(actionSuccessMsg(action));

      // Chain into the AppDirect booking for pushable actions.
      if (willPush) {
        const pushed = await pushRow();
        if (!pushed) {
          // Keep the drawer open on push failure/dry-run so the operator sees
          // the refreshed Push History + status instead of a vanished modal.
          await loadRow(rowId);
          onActioned?.();
          return;
        }
        await loadRow(rowId); // pull the stamped PS order id into the header pill
      }

      onActioned?.();
      onOpenChange(false);
      reset();
    } catch (e) {
      toast.error('Action failed');
    } finally {
      setSubmitting(false);
    }
  }

  function asDetailText(detail) {
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      const msgs = detail.map((d) => (typeof d === 'string' ? d : d?.msg)).filter(Boolean);
      if (msgs.length) return msgs.join('; ');
    }
    return null;
  }

  function actionSuccessMsg(action) {
    switch (action) {
      case 'approved':      return 'Match confirmed — booking in AppDirect…';
      case 'edited':        return 'Approved with edits — booking in AppDirect…';
      case 'rejected':      return 'Row dismissed';
      case 'create_new':    return 'Approved — creating customer + order in AppDirect…';
      case 'create_new_order': return 'Approved — creating order in AppDirect…';
      default:              return 'Action recorded';
    }
  }

  // ── Dismiss (reason dialog) ─────────────────────────────────────────────────
  function openReasonDialog(action) {
    setReasonText('');
    setReasonDialog({ open: true, action });
  }

  function submitReasonAction() {
    if (reasonDialog.action === 'rejected' && !reasonText.trim()) {
      toast.error('Please provide a dismissal reason.');
      return;
    }
    const action = reasonDialog.action;
    setReasonDialog({ open: false, action: null });
    handleAction(action, { reason: reasonText });
  }

  // ── Create New Customer ──────────────────────────────────────────────────────
  function openCreateCustomerModal() {
    if (!row) return;
    setCreateCustomerModal({
      open:  true,
      draft: row.output?.top_level || {},
    });
  }

  async function handleCustomerConfirm(editedDraft) {
    // editedDraft is flat:
    //   { name, advisor_company, sales_rep, sales_rep_id, street, city, state, zip }
    // Rebuild the nested top_level shape and merge into edited output.
    const nestedTopLevel = {
      advisor_company: editedDraft.advisor_company,
      sales_rep:       editedDraft.sales_rep,
      customer:        { name: editedDraft.name },
      location: {
        street: editedDraft.street,
        city:   editedDraft.city,
        state:  editedDraft.state,
        zip:    editedDraft.zip,
      },
    };
    const newEdited = { ...(edited || {}), top_level: nestedTopLevel };
    setEdited(newEdited);
    setCreateCustomerModal({ open: false, draft: null });

    // PATCH the rep id onto the row if the user picked one from the combobox
    if (editedDraft.sales_rep_id && rowId) {
      await api.patch(`${API_URL}/api/match/rows/${rowId}`, {
        sales_rep_id:   editedDraft.sales_rep_id,
        sales_rep_name: editedDraft.sales_rep,
      });
    }

    const extras = { edited_output: newEdited };
    if (hasPermission('match.row.edit')) {
      setPendingPush({ action: 'create_new', extras });
      return;
    }
    await handleAction('create_new', extras);
  }

  // ── Create New Order ─────────────────────────────────────────────────────────
  function openCreateOrderModal() {
    if (!row) return;
    setCreateOrderModal({
      open:  true,
      draft: row.output?.order_details || {},
    });
  }

  async function handleOrderConfirm(editedDraft) {
    // editedDraft may carry combobox picks (sales_rep_id, product_id) —
    // extract them for the row PATCH before merging the plain order fields.
    const { sales_rep_id, sales_rep_name, product_id, ...orderFields } = editedDraft;
    const newEdited = {
      ...(edited || {}),
      order_details: { ...(edited?.order_details || {}), ...orderFields },
    };
    setEdited(newEdited);
    setCreateOrderModal({ open: false, draft: null });

    const patch = {};
    if (sales_rep_id) {
      patch.sales_rep_id = sales_rep_id;
      patch.sales_rep_name = sales_rep_name;
    }
    if (product_id) {
      // The FK the push resolves first — also writes the product alias so
      // this provider text pre-resolves next time.
      patch.product_id = product_id;
      patch.product_name = orderFields.product || '';
    }
    if (Object.keys(patch).length > 0 && rowId) {
      await api.patch(`${API_URL}/api/match/rows/${rowId}`, patch);
    }

    const extras = { edited_output: newEdited };
    if (hasPermission('match.row.edit')) {
      setPendingPush({ action: 'create_new_order', extras });
      return;
    }
    await handleAction('create_new_order', extras);
  }

  // ── Complete-required-fields confirm ────────────────────────────────────────
  function fillActionLabel(action) {
    switch (action) {
      case 'create_new':       return 'Create Customer';
      case 'create_new_order': return 'Create Order';
      default:                 return 'Confirm Match';
    }
  }

  async function handleFillConfirm(values) {
    const { action, extras } = fillModal;
    if (!action || !rowId) return;
    setSubmitting(true);
    try {
      // Row-level fields persist via PATCH (also feeds the product-alias
      // learning loop); Contract H fields ride the edited-output overlay.
      const patch = {};
      if (values.product?.id) {
        patch.product_id = values.product.id;
        patch.product_name = values.product.name;
      }
      if (values.sales_rep?.id) {
        patch.sales_rep_id = values.sales_rep.id;
        patch.sales_rep_name = values.sales_rep.name;
      }
      if (Object.keys(patch).length > 0) {
        const res = await api.patch(`${API_URL}/api/match/rows/${rowId}`, patch);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          toast.error(asDetailText(err?.detail) || 'Failed to save fields');
          return;
        }
      }

      let newEdited =
        extras?.edited_output ||
        (isEdited ? edited : structuredClone(row?.output || {})) ||
        {};
      if (values.contract_term) {
        newEdited = setDot(newEdited, 'order_details.contract_term', values.contract_term);
      }
      if (values.contract_signed) {
        newEdited = setDot(newEdited, 'order_details.contract_signed', values.contract_signed);
      }
      if (values.advisor_company) {
        newEdited = setDot(newEdited, 'top_level.advisor_company', values.advisor_company);
        fetchSalesReps(values.advisor_company);
      }
      if (values.customer_name) {
        newEdited = setDot(newEdited, 'top_level.customer.name', values.customer_name);
      }
      setEdited(newEdited);
      setFillModal({ open: false, readiness: null, action: null, extras: null });

      // Re-enters the readiness check with the fixes applied — if the server
      // is satisfied the action fires; if not, the modal reopens with an
      // up-to-date missing list instead of guessing client-side.
      await handleAction(action, { ...(extras || {}), edited_output: newEdited });
    } finally {
      setSubmitting(false);
    }
  }

  // Push-confirm copy — computed once per render off pendingPush/row so the
  // dialog below doesn't call pushConfirmCopy four times over.
  const pushCopy = pendingPush ? pushConfirmCopy(pendingPush.action, row) : null;

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          onOpenChange(v);
          if (!v) reset();
        }}
      >
        <DialogContent
          className="!max-w-5xl w-[90vw] max-h-[92vh] p-0 flex flex-col bg-boss-surface border border-boss-border text-boss-text overflow-hidden"
          data-testid="row-detail-drawer"
        >
          {/* ── Header ─────────────────────────────────────────────────────── */}
          <DialogHeader className="px-6 py-4 border-b border-boss-border shrink-0">
            <div className="flex items-start justify-between gap-4">
              {/* Left: row title + meta */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  {/* Row N label */}
                  <span className="text-[12px] font-medium text-boss-text-muted font-mono">
                    Row {row?.row_index ?? '…'}
                  </span>
                  {/* Status pill */}
                  {row && (
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${statusClass(row.status)}`}
                      data-testid="row-detail-status-pill"
                    >
                      {STATUS_LABEL[row.status] || row.status}
                    </span>
                  )}
                  {/* row_id in mono */}
                  {row && (
                    <span
                      className="text-[10px] font-mono text-boss-text-muted hidden sm:inline"
                      data-testid="row-detail-row-id"
                    >
                      {row.row_id}
                    </span>
                  )}
                  {row?.session_id && (
                    <span className="text-[10px] font-mono text-boss-text-muted hidden md:inline">
                      Session: {row.session_id}
                    </span>
                  )}
                </div>

                {/* Customer — Product title (the record's identity; the address
                    moved down to the chip row — operators scan by who/what,
                    not by street) */}
                <DialogTitle
                  className="text-[18px] font-semibold leading-tight text-boss-text truncate"
                  data-testid="row-detail-header-title"
                >
                  {row
                    ? [
                        getDot(row.output, 'top_level.customer.name'),
                        getDot(row.output, 'order_details.product'),
                      ].filter(Boolean).join(' — ') ||
                      headerAddress ||
                      `Row #${row.row_index}`
                    : 'Loading…'}
                </DialogTitle>

                {/* Provider · Address chips */}
                <DialogDescription asChild>
                  <div>
                    {row && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[12px]">
                        <MetaBadge label="Provider" value={getDot(row.output, 'order_details.provider')} />
                        <MetaBadge label="Address"  value={headerAddress} />
                      </div>
                    )}
                  </div>
                </DialogDescription>
              </div>

              {/* Right: PSO pill + Detail ID + Confidence + close */}
              {row && (
                <div className="flex flex-col items-end gap-2 shrink-0">
                  {/* Provider Sales Order ID — top-level field post-push; fall back to
                      match-hint nested in output before the push has completed. */}
                  <PsoCopyPill value={
                    row.appdirect_order_id ||
                    getDot(row.output, 'order_details.appdirect_order_id') ||
                    null
                  } />

                  {/* Detail ID */}
                  <span
                    className="text-[11px] font-mono uppercase tracking-wider text-boss-text-muted"
                    data-testid="row-detail-header-detail-id"
                  >
                    Detail ID: {detailId}
                  </span>

                  {/* Confidence bar */}
                  <div className="flex items-center gap-2">
                    <ConfidenceBar
                      score={row.audit_metadata?.confidence_score ?? 0}
                      rowId={row.row_id}
                      size="md"
                    />
                    {(row.audit_metadata?.flags || []).includes('needs_review') && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-[var(--boss-warning-border)] bg-[var(--boss-warning-soft)] px-2 py-0.5 text-[11px] text-[var(--boss-warning-text)]">
                        <AlertTriangle className="h-3 w-3" />
                        Review
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </DialogHeader>

          {/* ── Scrollable body ────────────────────────────────────────────── */}
          <div className="flex-1 overflow-y-auto">
            {loading || !row ? (
              <div className="flex items-center justify-center py-16 text-boss-text-muted">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                <span className="text-[13px]">Loading row…</span>
              </div>
            ) : (
              <div className="px-6 py-4 space-y-4">
                {/* Review reason banner */}
                {row.review_reason && (
                  <div
                    className="rounded-lg border border-[var(--boss-warning-border)] bg-[var(--boss-warning-soft)] px-3 py-2.5 text-[13px] text-[var(--boss-warning-text)] flex gap-2 items-start"
                    data-testid="row-detail-review-reason"
                  >
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <div>
                      <div className="font-semibold mb-0.5">Needs review</div>
                      <div>{row.review_reason}</div>
                    </div>
                  </div>
                )}

                {/* Report-vs-matcher contradiction: "Existing Customer-MACD"
                    is the PROVIDER's ledger; "no match" is Provider Sales.
                    Usually a missed match — say so before the reviewer
                    creates a duplicate customer. */}
                {(row.flags || []).includes('type_conflict') && (
                  <div
                    className="rounded-lg border border-[var(--boss-warning-border)] bg-[var(--boss-warning-soft)] px-3 py-2.5 text-[13px] text-[var(--boss-warning-text)] flex gap-2 items-start"
                    data-testid="row-detail-type-conflict"
                  >
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <div>
                      <div className="font-semibold mb-0.5">
                        Report says existing customer — no Provider Sales match found
                      </div>
                      <div>
                        The provider's order type ({row.output?.order_details?.contract_type})
                        claims this is existing business, but the AI found no matching customer in
                        Provider Sales. Check the candidates below for a missed match (different
                        name/DBA) before creating a new customer.
                      </div>
                    </div>
                  </div>
                )}

                {/* Flag chips */}
                {row.flags?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5" data-testid="row-detail-flags">
                    {row.flags.map((f) => (
                      <FlagChip key={f} flag={f} rowId={row.row_id} />
                    ))}
                  </div>
                )}

                {/* Score breakdown */}
                <ScoreBreakdown row={row} />

                {/* 5-tab comparison grid */}
                <Tabs
                  value={activeTab}
                  onValueChange={setActiveTab}
                  className="w-full"
                >
                  <TabsList
                    className="h-9 bg-boss-elevated border border-boss-border"
                    data-testid="row-detail-tabs"
                  >
                    {TABS.map((t) => (
                      <TabsTrigger
                        key={t.key}
                        value={t.key}
                        className="text-[12px] data-[state=active]:bg-boss-surface data-[state=active]:text-boss-text text-boss-text-muted"
                        data-testid={`row-detail-tab-${t.key}`}
                      >
                        {t.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  {TABS.map((t) => (
                    <TabsContent key={t.key} value={t.key} className="pt-4">
                      <FieldGrid
                        fields={FIELDS_BY_TAB[t.key] || []}
                        source={row.source_row || {}}
                        edited={edited || {}}
                        original={row.output || {}}
                        flags={row.audit_metadata?.flags || []}
                        isNewCustomer={isNewCustomer}
                        matchedCandidateId={matchedCandidateId}
                        onChange={(path, value) =>
                          setEdited((cur) => setDot(cur || {}, path, value))
                        }
                        providerProducts={providerProducts}
                        productMatch={productMatch}
                        salesReps={salesReps}
                        onProductSelect={handleProductSelect}
                        onSalesRepSelect={handleSalesRepSelect}
                        catalogWarning={catalogWarning}
                        productsFetchedAt={providerProductsFetchedAt}
                        onRefreshProducts={handleRefreshProducts}
                        refreshingProducts={refreshingProducts}
                      />
                    </TabsContent>
                  ))}
                </Tabs>

                {/* Candidates considered (collapsible) */}
                {Array.isArray(row.audit_metadata?.candidates_considered) &&
                  row.audit_metadata.candidates_considered.length > 0 && (
                    <Collapsible defaultOpen={false}>
                      <CollapsibleTrigger asChild>
                        <button
                          type="button"
                          className="flex items-center gap-2 text-[13px] text-boss-text-muted hover:text-boss-text transition-colors"
                          data-testid="row-detail-candidates-toggle"
                        >
                          <ChevronDown className="h-4 w-4" />
                          Candidates considered (
                          {row.audit_metadata.candidates_considered.length})
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="mt-3">
                          <CandidatesConsidered
                            candidates={row.audit_metadata.candidates_considered}
                            flags={row.audit_metadata?.flags || []}
                          />
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  )}

                {/* Push history */}
                <div className="pt-2">
                  <PushHistorySection rowId={row.row_id} />
                </div>
              </div>
            )}
          </div>

          {/* ── Sticky action bar ──────────────────────────────────────────── */}
          {row && (
            <div className="shrink-0 border-t border-boss-border bg-boss-surface px-6 py-4 flex items-center justify-between gap-3">
              {/* Edited indicator */}
              <div className="text-[12px] text-boss-text-muted">
                {isEdited ? (
                  <span className="inline-flex items-center gap-1 text-[var(--boss-info-text)]">
                    <Pencil className="h-3 w-3" />
                    Edited — changes saved on Confirm
                  </span>
                ) : (
                  <span className="text-boss-text-muted">Pristine</span>
                )}
              </div>

              {/* Action buttons — gated on match.row.change_status (the server-side
                  slug that enforces all four mutations). Backend enforces too; the
                  client gate is defense-in-depth + visibly disables for view-only. */}
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {/* Dismiss */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openReasonDialog('rejected')}
                  disabled={submitting || !hasPermission('match.row.change_status')}
                  className="border-[var(--boss-danger-border)] text-[var(--boss-danger-text)] hover:bg-[var(--boss-danger-soft)]"
                  data-testid="row-detail-reject-button"
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Dismiss
                </Button>

                {/* Create New Customer */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openCreateCustomerModal}
                  disabled={submitting || !hasPermission('match.row.change_status')}
                  className="border-[var(--boss-info-border)] text-[var(--boss-info-text)] hover:bg-[var(--boss-info-soft)]"
                  data-testid="row-detail-create-new-button"
                >
                  <UserPlus className="h-4 w-4 mr-1" />
                  {isNewCustomer ? 'Create New Customer' : 'No Match — Create Customer'}
                </Button>

                {/* Create New Order */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openCreateOrderModal}
                  disabled={submitting || !hasPermission('match.row.change_status')}
                  className="border-[var(--boss-info-border)] text-[var(--boss-info-text)] hover:bg-[var(--boss-info-soft)]"
                  data-testid="row-detail-create-new-order-button"
                >
                  <FilePlus className="h-4 w-4 mr-1" />
                  Create New Order
                </Button>

                {/* Confirm Match — Enrich */}
                <Button
                  size="sm"
                  onClick={() => {
                    const action = isEdited ? 'edited' : 'approved';
                    // Only actions that will actually chain into pushRow() need
                    // the confirm gate — mirrors the willPush check in
                    // handleAction so view-only-for-push users aren't stopped
                    // by a confirm dialog that would never lead to a write.
                    if (hasPermission('match.row.edit')) {
                      setPendingPush({ action, extras: {} });
                      return;
                    }
                    handleAction(action);
                  }}
                  disabled={submitting || !hasPermission('match.row.change_status')}
                  className="bg-[var(--boss-success)] text-boss-text-inverse hover:brightness-90"
                  data-testid="row-detail-approve-button"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : isEdited ? (
                    <Pencil className="h-4 w-4 mr-1" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                  )}
                  Confirm Match — Enrich
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Reason dialog (Dismiss) ─────────────────────────────────────────── */}
      <Dialog
        open={reasonDialog.open}
        onOpenChange={(v) => setReasonDialog({ open: v, action: reasonDialog.action })}
      >
        <DialogContent
          className="sm:max-w-[480px] bg-boss-surface border border-boss-border text-boss-text"
          data-testid="row-detail-reason-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-boss-text">
              {reasonDialog.action === 'rejected' ? 'Dismiss row' : 'Reason'}
            </DialogTitle>
            <DialogDescription className="text-boss-text-secondary">
              Add a brief reason. This will be recorded with the action so the AI can learn.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            placeholder="Reason…"
            className="boss-input min-h-[120px]"
            data-testid="row-detail-reason-input"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReasonDialog({ open: false, action: null })}
              className="border-boss-border text-boss-text-secondary hover:text-boss-text"
            >
              Cancel
            </Button>
            <Button
              onClick={submitReasonAction}
              className="btn-primary"
              data-testid="row-detail-reason-submit"
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Create New Customer modal ───────────────────────────────────────── */}
      <CreateNewCustomerModal
        open={createCustomerModal.open}
        onOpenChange={(v) => !v && setCreateCustomerModal({ open: false, draft: null })}
        draft={createCustomerModal.draft}
        submitting={submitting}
        onConfirm={handleCustomerConfirm}
        salesReps={salesReps}
        onFetchSalesReps={fetchSalesReps}
      />

      {/* ── Create New Order modal ──────────────────────────────────────────── */}
      <CreateNewOrderModal
        open={createOrderModal.open}
        onOpenChange={(v) => !v && setCreateOrderModal({ open: false, draft: null })}
        draft={createOrderModal.draft}
        submitting={submitting}
        onConfirm={handleOrderConfirm}
        salesReps={salesReps}
        advisorCompany={advisorCompany}
        onFetchSalesReps={fetchSalesReps}
        providerProducts={providerProducts}
        catalogWarning={catalogWarning}
      />

      {/* ── Complete-required-fields modal (fill-in-place confirm) ──────────── */}
      <CompleteRequiredFieldsModal
        open={fillModal.open}
        onOpenChange={(v) =>
          !v && setFillModal({ open: false, readiness: null, action: null, extras: null })
        }
        readiness={fillModal.readiness}
        actionLabel={fillActionLabel(fillModal.action)}
        providerProducts={providerProducts}
        catalogWarning={catalogWarning}
        salesReps={
          salesRepsCache[edited?.top_level?.advisor_company || advisorCompany] ?? null
        }
        submitting={submitting}
        onConfirm={handleFillConfirm}
      />

      {/* ── Confirm before push — gates every action that chains into a real
          AppDirect write. Follows the same onConfirm/onCancel idiom as the
          delete confirms in ProviderTemplatesTab/SessionsTab: ConfirmModal
          always calls onCancel right after onConfirm, so onCancel is what
          actually clears pendingPush. Copy/variant computed by
          pushConfirmCopy from the row's appdirect_active_env /
          appdirect_live_post_enabled — see that function for the four
          states it distinguishes. ──────────────────────────────────────── */}
      <ConfirmModal
        isOpen={!!pendingPush}
        title={pushCopy?.title || ''}
        message={pushCopy?.message || ''}
        confirmText={pushCopy?.confirmText || 'Push to AppDirect'}
        cancelText="Cancel"
        variant={pushCopy?.variant || 'warning'}
        onConfirm={() => pendingPush && handleAction(pendingPush.action, pendingPush.extras)}
        onCancel={() => setPendingPush(null)}
      />
    </>
  );
}

// ── Internal sub-components ───────────────────────────────────────────────────

function MetaBadge({ label, value }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-boss-elevated border border-boss-border px-2 py-0.5 text-[12px] text-boss-text-muted">
      {label}:{' '}
      <strong className="font-medium text-boss-text">{value || '—'}</strong>
    </span>
  );
}

function PsoCopyPill({ value }) {
  const hasPso = value != null && value !== '';
  return (
    <div
      className={`flex items-baseline gap-2 rounded-lg border px-3 py-1.5 ${
        hasPso
          ? 'border-boss-status-info/40 bg-boss-status-info-bg'
          : 'border-dashed border-boss-border bg-boss-elevated/60'
      }`}
      data-testid="row-detail-header-pso"
    >
      <span className="text-[10px] uppercase tracking-wider text-boss-text-muted font-medium">
        Provider Sales Order ID
      </span>
      {hasPso ? (
        <button
          type="button"
          onClick={() => {
            navigator.clipboard.writeText(String(value)).catch(() => null);
            toast.success('Copied to clipboard');
          }}
          className="inline-flex items-center gap-1 font-mono text-[14px] font-semibold text-boss-text hover:text-boss-status-info transition-colors"
          data-testid="row-detail-pso-copy-btn"
          aria-label={`Copy Provider Sales Order ID ${value}`}
        >
          <Clipboard className="h-3 w-3" />
          {value}
        </button>
      ) : (
        <span className="font-mono text-[13px] text-boss-text-muted italic">
          — pending —
        </span>
      )}
    </div>
  );
}
